import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "./ui/sheet";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "./ui/alert-dialog";
import { api, formatApiErrorDetail, formatMXN } from "../lib/api";
import { toast } from "sonner";
import { ArrowDownLeft, ArrowUpRight, Trash2, Plus } from "lucide-react";
import { useScope } from "../context/ScopeContext";

export default function EditTransactionDialog({ open, onOpenChange, tx, accounts, onChanged }) {
  const { scope } = useScope();
  const [type, setType] = useState("gasto");
  const [accountId, setAccountId] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!tx) return;
    setType(tx.type);
    setAccountId(tx.account_id);
    setCategoryId(tx.category_id || "");
    setAmount(String(tx.amount));
    setNote(tx.note || "");
  }, [tx]);

  // Fetch categories for the current type
  useEffect(() => {
    if (!open || !tx || tx.type === "transferencia") return;
    if (type === "transferencia") return;
    api.get("/categories", { params: { scope, kind: type } })
      .then(({ data }) => setCategories(data))
      .catch(() => setCategories([]));
  }, [open, scope, type, tx]);

  // Reset confirm state on close
  useEffect(() => {
    if (!open) {
      setConfirmDelete(false);
      setDeleting(false);
    }
  }, [open]);

  if (!tx) return null;
  const readOnly = tx.type === "transferencia";

  const handleSave = async (e) => {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) {
      toast.error("El monto debe ser mayor a 0");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type,
        account_id: accountId,
        note,
        amount: Number(amount),
        category_id: categoryId || null,
      };
      await api.patch(`/transactions/${tx.id}`, payload);
      toast.success("Movimiento actualizado");
      onOpenChange(false);
      onChanged?.();
      window.dispatchEvent(new CustomEvent("fp:tx-created"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const runDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/transactions/${tx.id}`);
      toast.success("Movimiento eliminado");
      setConfirmDelete(false);
      onOpenChange(false);
      onChanged?.();
      window.dispatchEvent(new CustomEvent("fp:tx-created"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al eliminar");
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  // Find original category snapshot to display when it's been archived/removed from list
  const selectedCategoryInList = categories.some((c) => c.id === categoryId);
  const stockCategorySnapshot = tx.category && !selectedCategoryInList && categoryId
    ? tx.category
    : null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          data-testid="edit-tx-sheet"
          className="bg-bone border-line rounded-t-3xl p-0 max-w-md mx-auto overflow-y-auto overscroll-contain"
          style={{ maxHeight: "92dvh", paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 1.5rem)" }}
        >
          <div className="mx-auto mt-3 mb-1 h-1.5 w-12 rounded-full bg-line/80" aria-hidden />
          <SheetHeader className="text-left px-6 pt-2">
            <SheetTitle className="font-display text-2xl text-ink">
              {readOnly ? "Transferencia" : "Editar movimiento"}
            </SheetTitle>
            <SheetDescription className="text-soft text-sm">
              {readOnly
                ? "Las transferencias no se editan. Puedes eliminarla y crear una nueva."
                : "Cambia los datos o elimínalo. Si pertenece a un mes cerrado, el servidor bloqueará la operación."}
            </SheetDescription>
          </SheetHeader>

          {!readOnly && (
            <form onSubmit={handleSave} className="space-y-4 px-6 mt-2">
              <div className="grid grid-cols-2 gap-2 bg-white border border-line rounded-2xl p-1">
                <button
                  type="button"
                  data-testid="edit-tx-type-gasto"
                  onClick={() => setType("gasto")}
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                    type === "gasto" ? "bg-sage-900 text-bone" : "text-ink"
                  }`}
                >
                  <ArrowUpRight size={14} /> Gasto
                </button>
                <button
                  type="button"
                  data-testid="edit-tx-type-ingreso"
                  onClick={() => setType("ingreso")}
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                    type === "ingreso" ? "bg-sage-900 text-bone" : "text-ink"
                  }`}
                >
                  <ArrowDownLeft size={14} /> Ingreso
                </button>
              </div>

              <Input label="Monto" testid="edit-tx-amount" type="number" step="0.01" value={amount} onChange={setAmount} />
              <p className="text-xs text-soft -mt-2">{formatMXN(Number(amount) || 0)}</p>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-1">
                  Cuenta
                </label>
                <select
                  data-testid="edit-tx-account"
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  className="w-full bg-white border border-line rounded-xl px-3 py-3 outline-none focus:border-sage-500"
                >
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-2">
                  Categoría
                </label>
                {stockCategorySnapshot && (
                  <p className="text-[11px] text-soft mb-2 leading-relaxed">
                    Capturado como <span className="font-bold text-ink">{stockCategorySnapshot}</span>. Esa categoría ya no está activa; puedes asignar otra o dejarlo sin categoría.
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setCategoryId("")}
                    data-testid="edit-tx-category-none"
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      categoryId === ""
                        ? "bg-sage-900 text-bone border-sage-900"
                        : "bg-white border-line text-soft"
                    }`}
                  >
                    Sin categoría
                  </button>
                  {categories.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setCategoryId(c.id)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                        categoryId === c.id
                          ? "bg-sage-900 text-bone border-sage-900"
                          : "bg-white border-line text-ink"
                      }`}
                    >
                      {c.name}
                    </button>
                  ))}
                  <Link
                    to="/categorias"
                    onClick={() => onOpenChange(false)}
                    className="px-3 py-1.5 rounded-full text-sm border border-dashed border-line text-soft hover:border-sage-300 hover:text-sage-700 inline-flex items-center gap-1.5"
                  >
                    <Plus size={12} /> Nueva
                  </Link>
                </div>
              </div>

              <Input label="Nota" testid="edit-tx-note" value={note} onChange={setNote} placeholder="Detalles" />

              <button
                type="submit"
                disabled={saving}
                data-testid="edit-tx-save"
                className="w-full bg-sage-900 text-bone rounded-2xl py-3 font-bold hover:bg-sage-900/90 disabled:opacity-60"
              >
                {saving ? "Guardando…" : "Guardar cambios"}
              </button>
            </form>
          )}

          <div className="px-6 mt-4">
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={deleting}
              data-testid="edit-tx-delete"
              className="w-full inline-flex items-center justify-center gap-2 bg-clay/10 text-clay font-bold py-3 rounded-2xl hover:bg-clay/15 disabled:opacity-60"
            >
              <Trash2 size={14} /> Eliminar movimiento
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={confirmDelete}
        onOpenChange={(o) => { if (!o && !deleting) setConfirmDelete(false); }}
      >
        <AlertDialogContent
          className="max-w-sm bg-bone border-line rounded-3xl"
          data-testid="confirm-tx-delete-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">
              ¿Eliminar este movimiento?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-soft text-sm leading-relaxed">
              Se quitará del historial y se revertirá su efecto sobre el saldo de la cuenta. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2 mt-2">
            <AlertDialogCancel
              disabled={deleting}
              data-testid="confirm-tx-cancel"
              className="bg-white border-line text-ink rounded-2xl font-bold"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runDelete(); }}
              disabled={deleting}
              data-testid="confirm-tx-confirm"
              className="bg-clay text-bone hover:bg-clay/90 rounded-2xl font-bold"
            >
              {deleting ? "Eliminando…" : "Sí, eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Input({ label, testid, type = "text", step, value, onChange, placeholder }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-1">
        {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white border border-line rounded-xl px-3 py-3 outline-none focus:border-sage-500"
      />
    </div>
  );
}
